/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';
import { FC, ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import { getAppDomainHost } from 'utils/getAppDomainHost';

import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import { addDocument, updateDocument } from 'modules/documents/redux/documentActions';
import { updateBeneficiary } from 'modules/incentivesMarket/api/beneficiaryActions';
import { subcategoryTypes } from 'modules/incentivesMarket/constants/incentiveOfferOperationDocumentTypes';
import { createIncentiveOfferOperation } from 'modules/incentivesMarket/redux/incentiveOfferOperationActions';
import { collectPublisherAuditContext } from 'modules/incentivesMarket/utils/collectPublisherAuditContext';
import { resolveIncentiveOfferOperationParties } from 'modules/incentivesMarket/utils/resolveIncentiveOfferOperationParties';
import { getIncentiveOfferAcceptTotalPrice } from 'modules/incentivesMarket/utils/getIncentiveOfferAcceptTotalPrice';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { ScreenSizes } from 'constants/sizes';
import { AcceptIncentiveOfferModalContextType } from '../types/AcceptIncentiveOfferModalContextType';

const defaultContextValue: AcceptIncentiveOfferModalContextType = {
  data: {
    offer: {} as IncentiveOffer,
    beneficiaryBuyer: null,
    purchaseQuantity: 0,
    currentStep: 1,
    isTermsAccepted: false,
    isPurchaseContractAccepted: false,
    isIrrevocableAccepted: false,
    isFiscalAccepted: false,
    isDataVerified: false,
    isLoading: false,
    offerReference: '',
    sellerName: '',
    buyerPlansLabel: '',
    sellerPlans: [],
    totalPrice: 0,
    buyerDocuments: [],
  },
  actions: {
    handleCloseModal: () => {},
    handleNextStep: () => {},
    handlePrevStep: () => {},
    handleToggleTerms: () => {},
    handleTogglePurchaseContract: () => {},
    handleToggleIrrevocable: () => {},
    handleToggleFiscal: () => {},
    handleToggleDataVerified: () => {},
    handleAddDocument: () => {},
    handleRemoveDocument: () => {},
    handleUpdatePartnerBuyerField: () => {},
    handleUpdatePartnerBuyerAddressField: () => {},
    handleConfirmPurchase: async () => {},
  },
};

export const AcceptIncentiveOfferModalContext =
  createContext<AcceptIncentiveOfferModalContextType>(defaultContextValue);

type AcceptIncentiveOfferModalProviderProps = {
  children: ReactNode;
  offer: IncentiveOffer;
  beneficiaryBuyer: Partial<IncentivesMarketBeneficiary> | null;
  purchaseQuantity: number;
  societyId: string;
  offerReference: string;
  sellerName: string;
  buyerPlansLabel: string;
  sellerPlans?: IncentivesMarketPlanContext[];
  onSuccess?: () => void;
  size?: ScreenSizes;
};

export const AcceptIncentiveOfferModalProvider: FC<
  AcceptIncentiveOfferModalProviderProps
> = ({
  children,
  offer,
  beneficiaryBuyer,
  purchaseQuantity,
  societyId,
  offerReference,
  sellerName,
  buyerPlansLabel,
  sellerPlans = [],
  onSuccess,
}) => {
  const { t } = useTranslate();

  const totalPrice = useMemo(
    () =>
      getIncentiveOfferAcceptTotalPrice({
        offer,
        purchaseQuantity,
        sellerPlans,
      }),
    [offer, purchaseQuantity, sellerPlans]
  );
  const user = useSelector((state: { user?: { _id?: string } }) => state.user);

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isPurchaseContractAccepted, setIsPurchaseContractAccepted] =
    useState(false);
  const [isIrrevocableAccepted, setIsIrrevocableAccepted] = useState(false);
  const [isFiscalAccepted, setIsFiscalAccepted] = useState(false);
  const [isDataVerified, setIsDataVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buyerDocuments, setBuyerDocuments] = useState<File[]>([]);
  const [editablePartnerBuyer, setEditablePartnerBuyer] = useState<
    Partial<IncentivesMarketBeneficiary> | null
  >(
    beneficiaryBuyer
      ? {
          ...beneficiaryBuyer,
          address: beneficiaryBuyer.address
            ? { ...beneficiaryBuyer.address }
            : {
                line1: '',
                zip: '',
                city: '',
                state: '',
                country: '',
              },
        }
      : null
  );

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleAddDocument = (file: File) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (file.size > maxSize) {
      errorSwal(t('FileTooLargeMax5MB'));
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      errorSwal(t('InvalidFileTypePDFJPGPNG'));
      return;
    }
    setBuyerDocuments((prev) => [...prev, file]);
  };

  const handleRemoveDocument = (index: number) => {
    setBuyerDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePartnerBuyerField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  const handleUpdatePartnerBuyerAddressField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) => {
      if (!prev) return null;
      const currentAddress = prev.address || {
        line1: '',
        zip: '',
        city: '',
        state: '',
        country: '',
      };
      return {
        ...prev,
        address: { ...currentAddress, [field]: value },
      };
    });
  };

  const handleConfirmPurchase = async () => {
    const buyerToUse = editablePartnerBuyer || beneficiaryBuyer;
    if (!buyerToUse) return;

    const participantId = buyerToUse._id || beneficiaryBuyer?._id;
    const publisherId =
      typeof offer.beneficiary === 'string' ? offer.beneficiary : offer.beneficiary._id;

    if (!participantId || !publisherId) return;

    const isSellOffer = offer.type === 'SELL';
    const { buyer, seller } = resolveIncentiveOfferOperationParties({
      offerType: offer.type,
      publisherBeneficiaryId: publisherId,
      participantBeneficiaryId: participantId,
    });

    try {
      setIsLoading(true);

      const participantDocumentIds: string[] = [];

      for (const file of buyerDocuments) {
        const fileType =
          fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user?._id,
          society: societyId,
          fileType,
          category: documentTypes.DOCUMENTS,
          subcategory: subcategoryTypes.TRADING_OFFER_OPERATION.IDENTITY_DOCUMENT,
          description: t('IdentityDocumentForIncentiveOffer'),
        };
        const uploadedDoc = await store.dispatch(addDocument(newDocument));
        if (uploadedDoc?._id) {
          participantDocumentIds.push(uploadedDoc._id);
        }
      }

      const participantUpdateData = {
        name: buyerToUse.name,
        cif: buyerToUse.cif,
        email: buyerToUse.email,
        phone: buyerToUse.phone,
        address: buyerToUse.address,
        bankAccountNumber: buyerToUse.bankAccountNumber,
      };

      const operationData = {
        society: societyId,
        incentiveOffer: offer._id,
        buyer,
        seller,
        unitClass:
          typeof offer.unitClass === 'string'
            ? offer.unitClass
            : offer.unitClass?._id,
        unitsCount: purchaseQuantity,
        unitPrice: offer.unitPrice,
        ...(isSellOffer
          ? {
              buyerDocuments: participantDocumentIds,
              buyerUpdateData: participantUpdateData,
            }
          : {
              sellerDocuments: participantDocumentIds,
            }),
        participantAuditContext: collectPublisherAuditContext(),
        domain: getAppDomainHost(),
      };

      const createdOp = (await store.dispatch(
        createIncentiveOfferOperation(operationData)
      )) as IncentiveOfferOperation | undefined;

      if (!isSellOffer && participantId) {
        await store.dispatch(
          updateBeneficiary({
            beneficiaryId: participantId,
            updateBeneficiaryData: {
              ...participantUpdateData,
              userId: user?._id || '',
              societyId,
            },
          })
        );
      }

      if (createdOp?._id && participantDocumentIds.length > 0) {
        await Promise.all(
          participantDocumentIds.map((docId) =>
            store.dispatch(
              updateDocument(
                {
                  _id: docId,
                  linkedTo: getLinkedTo({
                    incentiveOfferOperationId: createdOp._id,
                  }),
                },
                false
              )
            )
          )
        );
      }

      setIsLoading(false);
      successSwal(t('PurchaseConfirmedSuccessfully'));
      handleCloseModal();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorConfirmingPurchase'));
      handleCloseModal();
    }
  };

  return (
    <AcceptIncentiveOfferModalContext.Provider
      value={{
        data: {
          offer,
          beneficiaryBuyer: editablePartnerBuyer ?? beneficiaryBuyer,
          purchaseQuantity,
          currentStep,
          isTermsAccepted,
          isPurchaseContractAccepted,
          isIrrevocableAccepted,
          isFiscalAccepted,
          isDataVerified,
          isLoading,
          offerReference,
          sellerName,
          buyerPlansLabel,
          sellerPlans,
          totalPrice,
          buyerDocuments,
        },
        actions: {
          handleCloseModal,
          handleNextStep,
          handlePrevStep,
          handleToggleTerms: () => setIsTermsAccepted((prev) => !prev),
          handleTogglePurchaseContract: () =>
            setIsPurchaseContractAccepted((prev) => !prev),
          handleToggleIrrevocable: () =>
            setIsIrrevocableAccepted((prev) => !prev),
          handleToggleFiscal: () => setIsFiscalAccepted((prev) => !prev),
          handleToggleDataVerified: () => setIsDataVerified((prev) => !prev),
          handleAddDocument,
          handleRemoveDocument,
          handleUpdatePartnerBuyerField,
          handleUpdatePartnerBuyerAddressField,
          handleConfirmPurchase,
        },
      }}
    >
      {children}
    </AcceptIncentiveOfferModalContext.Provider>
  );
};

export const useAcceptIncentiveOfferModal = () => {
  const context = useContext(AcceptIncentiveOfferModalContext);
  if (!context) {
    throw new Error(
      'useAcceptIncentiveOfferModal must be used within a AcceptIncentiveOfferModalProvider'
    );
  }
  return context;
};
